import md5 from 'md5';
import {
  CreateProposalParams,
  FlProposal,
  HotProposal,
  ProductProposal,
  Proposal,
  TransferProposal,
  TripSlotRequirement,
} from 'ts/shared/interfaces/CreateProposalParams';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { getReportList } from './proposal';
import { dateFormat, getBounds } from './utils';
import { addDays } from 'utils/utils';
import { formatISO } from 'date-fns';

export type TripSlot = {
  key: string;
  city: string;
  checkIn: string;
  checkOut: string;
};

export type TripBuilderFlightCandidate = {
  flight: FlProposal;
  index: number;
  route: string;
  isRoundTrip: boolean;
  airlineCode?: string;
};

export type TripBuilderTransferCandidate = {
  transfer: TransferProposal;
  index: number;
  city: string;
  day: string;
  dateTime: string;
  vehicle: string;
};

export type TripSlotContext = {
  rootSlotKey: string;
  chainNodes: Array<{
    slot: TripSlot;
    hotels: HotProposal[];
    transfers: TripBuilderTransferCandidate[];
  }>;
  suitableRoundTrips: Array<{
    flight: FlProposal;
    index: number;
    beforeData: { route: string; isRoundTrip: boolean; day: string };
    afterData: { route: string; day: string };
  }>;
  beforeCandidates: TripBuilderFlightCandidate[];
  afterCandidates: TripBuilderFlightCandidate[];
  middleFlightsByTransition: Record<string, TripBuilderFlightCandidate[]>;
};

export type TripBuilderActiveState = {
  activeHotelBySlot: Record<string, string>;
  activeTransferBySlot: Record<string, string>;
  activeBeforeFlightBySlot: Record<string, string>;
  activeAfterFlightBySlot: Record<string, string>;
  activeMiddleFlightByTransition: Record<string, string>;
  hotelImageBySlot?: Record<string, string>;
  proposalImage?: string;
};

export const emptyTripBuilderActiveState: TripBuilderActiveState = {
  activeHotelBySlot: {},
  activeTransferBySlot: {},
  activeBeforeFlightBySlot: {},
  activeAfterFlightBySlot: {},
  activeMiddleFlightByTransition: {},
};

export const parseTripBuilderSelectionsFromExternalParam = (
  externalParam_1?: string,
): TripBuilderActiveState | null => {
  if (!externalParam_1) {
    return null;
  }
  try {
    const data = JSON.parse(externalParam_1);
    if (!data.isTripBuilder) {
      return null;
    }
    const selections: TripBuilderActiveState = {
      activeHotelBySlot: data.activeHotelBySlot || {},
      activeTransferBySlot: data.activeTransferBySlot || {},
      activeBeforeFlightBySlot: data.activeBeforeFlightBySlot || {},
      activeAfterFlightBySlot: data.activeAfterFlightBySlot || {},
      activeMiddleFlightByTransition: data.activeMiddleFlightByTransition || {},
      hotelImageBySlot: data.hotelImageBySlot || {},
      proposalImage: data.proposalImage || '',
    };
    const hasHotelImages = Object.keys(selections.hotelImageBySlot || {}).length > 0;
    const hasProposalImage = !!selections.proposalImage;
    const hasAnySelection =
      hasProposalImage ||
      hasHotelImages ||
      Object.values({
        activeHotelBySlot: selections.activeHotelBySlot,
        activeTransferBySlot: selections.activeTransferBySlot,
        activeBeforeFlightBySlot: selections.activeBeforeFlightBySlot,
        activeAfterFlightBySlot: selections.activeAfterFlightBySlot,
        activeMiddleFlightByTransition: selections.activeMiddleFlightByTransition,
      }).some((group) => Object.keys(group).length > 0);
    return hasAnySelection ? selections : null;
  } catch {
    return null;
  }
};

export const tripRowKeys = {
  before: (rootSlotKey: string) => `before|${rootSlotKey}`,
  hotel: (slotKey: string) => `hotel|${slotKey}`,
  transfer: (slotKey: string) => `transfer|${slotKey}`,
  middle: (transitionKey: string) => `middle|${transitionKey}`,
  after: (rootSlotKey: string) => `after|${rootSlotKey}`,
};

export const getFlightKey = (flight: FlProposal, index: number) => `${flight.searchToken}-${index}`;

export const getHotelKey = (hotel: HotProposal, index: number) => `${hotel.searchToken}-${index}`;
export const getTransferKey = (transfer: TransferProposal, index: number) =>
  `${transfer.searchToken}-${index}`;

const getCity = (hotel: HotProposal) => {
  const cityName = hotel.item?.info?.cityName || hotel.item?.addressInfo?.city;
  return cityName ? String(cityName) : 'Unknown city';
};

const getCheckIn = (hotel: HotProposal) =>
  hotel.checkIn || hotel.item?.roomClasses?.[0]?.checkIn || '';
const getCheckOut = (hotel: HotProposal) =>
  hotel.checkOut || hotel.item?.roomClasses?.[0]?.checkOut || '';
const isSameCity = (flightCity: string, slotCity: string) => {
  const a = normalizeCity(flightCity);
  const b = normalizeCity(slotCity);
  if (!a || !b) {
    return false;
  }
  const aParts = a.split(' ');
  const bParts = b.split(' ');
  return a === b || a.includes(b) || b.includes(a) || aParts.includes(b) || bParts.includes(a);
};

export const toDayKey = (value: string) => (value ? dateFormat(value, 'yyyy-MM-dd', 'en') : '');
export const normalizeCity = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ');
export const getSlotKey = (city: string, checkIn: string, checkOut: string) =>
  `${normalizeCity(city)}|${toDayKey(checkIn)}|${toDayKey(checkOut)}`;

export const replaceSlotKeyCity = (
  slotKey: string,
  city: string,
  checkIn: string,
  checkOut: string,
) => {
  const parts = slotKey.split('|');
  if (parts.length >= 3) {
    return [normalizeCity(city), parts[parts.length - 2], parts[parts.length - 1]].join('|');
  }
  return getSlotKey(city, checkIn, checkOut);
};

export const getHotelSlotKey = (hotel: HotProposal) =>
  getSlotKey(getCity(hotel), getCheckIn(hotel), getCheckOut(hotel));

export const migrateRecordKey = <T>(record: Record<string, T>, oldKey: string, newKey: string) => {
  if (oldKey === newKey || record[oldKey] === undefined) {
    return record;
  }
  const next = { ...record };
  next[newKey] = next[oldKey];
  delete next[oldKey];
  return next;
};

export const migrateTransitionKeys = (
  record: Record<string, string>,
  oldSlotKey: string,
  newSlotKey: string,
) => {
  if (oldSlotKey === newSlotKey) {
    return record;
  }
  const next: Record<string, string> = {};
  Object.entries(record).forEach(([key, value]) => {
    const newKey = key
      .split('|')
      .map((part) => (part === oldSlotKey ? newSlotKey : part))
      .join('|');
    next[newKey] = value;
  });
  return next;
};

export { getCity, getCheckIn, getCheckOut, isSameCity };

export const getBeforeFlightData = (flight: FlProposal) => {
  const bounds = getBounds(flight.item);
  const first = bounds[0];
  return {
    day: toDayKey(first?.arrivalDateTime || ''),
    city: first?.arrivalAirport?.cityDesc || first?.arrivalAirport?.name || '',
    route: `${first?.departureAirport?.code || '-'} -> ${first?.arrivalAirport?.code || '-'}`,
    airlineCode: first?.airline?.code || flight.item?.outbound?.airline?.code || '',
    isRoundTrip: bounds.length > 1,
  };
};

export const getAfterFlightData = (flight: FlProposal) => {
  const bounds = getBounds(flight.item);
  const last = bounds[bounds.length - 1];
  return {
    day: toDayKey(last?.departureDateTime || ''),
    city: last?.departureAirport?.cityDesc || last?.departureAirport?.name || '',
    route: `${last?.departureAirport?.code || '-'} -> ${last?.arrivalAirport?.code || '-'}`,
    airlineCode: last?.airline?.code || '',
    isRoundTrip: bounds.length > 1,
  };
};

export const getFlightLegs = (flight: FlProposal) => {
  const bounds = getBounds(flight.item);
  return bounds.map((bound: any) => ({
    departureDay: toDayKey(bound?.departureDateTime || ''),
    arrivalDay: toDayKey(bound?.arrivalDateTime || ''),
    departureCity: bound?.departureAirport?.cityDesc || bound?.departureAirport?.name || '',
    arrivalCity: bound?.arrivalAirport?.cityDesc || bound?.arrivalAirport?.name || '',
    route: `${bound?.departureAirport?.code || '-'} -> ${bound?.arrivalAirport?.code || '-'}`,
    airlineCode: bound?.airline?.code || '',
  }));
};

export const getTransferData = (
  transfer: TransferProposal,
  index: number,
): TripBuilderTransferCandidate => {
  const forward = transfer.forwardItem?.transfer;
  const dateTime = forward?.pickUpDate || forward?.pickUpDetails?.pickUpDateTime || '';
  const city = [
    forward?.pickUpDetails?.city?.desc,
    forward?.dropOffDetails?.city?.desc,
    transfer.forwardItem?.mainCity?.desc,
    forward?.pickUpDetails?.airport?.desc,
    forward?.dropOffDetails?.airport?.desc,
  ]
    .filter((value): value is string => !!value)
    .join(' ');
  const vehicle = forward?.transferVehicles?.[0]?.vehicle?.desc || 'Transfer';
  return {
    transfer,
    index,
    city,
    day: toDayKey(dateTime),
    dateTime,
    vehicle,
  };
};

export const matchesHotelSlot = (
  hotel: HotProposal,
  city: string,
  checkIn: string,
  checkOut: string,
) =>
  isSameCity(getCity(hotel), city) &&
  toDayKey(getCheckIn(hotel) || '') === toDayKey(checkIn || '') &&
  toDayKey(getCheckOut(hotel) || '') === toDayKey(checkOut || '');

export const matchesFlightBeforeSlot = (flight: FlProposal, checkIn: string, city: string) => {
  const data = getBeforeFlightData(flight);
  if (data.day !== toDayKey(checkIn || '')) {
    return false;
  }
  if (!city || !data.city) {
    return true;
  }
  return isSameCity(data.city, city);
};

export const matchesFlightAfterSlot = (flight: FlProposal, checkOut: string, city: string) => {
  const data = getAfterFlightData(flight);
  if (data.day !== toDayKey(checkOut || '')) {
    return false;
  }
  if (!city || !data.city) {
    return true;
  }
  return isSameCity(data.city, city);
};

export const matchesFlightMiddleTransition = (
  flight: FlProposal,
  transitionDay: string,
  fromCity: string,
  toCity: string,
) =>
  getFlightLegs(flight).some(
    (leg: any) =>
      leg.departureDay === toDayKey(transitionDay || '') &&
      isSameCity(leg.departureCity, fromCity) &&
      isSameCity(leg.arrivalCity, toCity),
  );

export const matchesTransferSlot = (
  transfer: TransferProposal,
  city: string,
  checkIn: string,
  checkOut: string,
) => {
  const data = getTransferData(transfer, 0);
  return (
    !!data.day &&
    data.day >= toDayKey(checkIn || '') &&
    data.day <= toDayKey(checkOut || '') &&
    isSameCity(data.city, city)
  );
};

export const defaultTripSlotRequirement: TripSlotRequirement = 'mandatory-first';

export const tripRequirementToPType = (
  requirement: TripSlotRequirement,
): NonNullable<ProductProposal['pType']> => {
  if (requirement === 'optional') {
    return 'Optional';
  }
  if (requirement === defaultTripSlotRequirement) {
    return 'ManadatoryFirst';
  }
  return 'Mandatory';
};

export const buildTripSlotContexts = (proposal: Proposal): TripSlotContext[] => {
  const slotsMap = new Map<string, TripSlot>();
  proposal.hotels.forEach((hotel) => {
    const city = getCity(hotel);
    const checkIn = getCheckIn(hotel);
    const checkOut = getCheckOut(hotel);
    const key = getSlotKey(city, checkIn, checkOut);
    if (!slotsMap.has(key)) {
      slotsMap.set(key, { key, city, checkIn, checkOut });
    }
  });
  const slots = Array.from(slotsMap.values()).sort((a, b) =>
    toDayKey(a.checkIn).localeCompare(toDayKey(b.checkIn)),
  );

  const slotHotelsMap = new Map<string, HotProposal[]>();
  proposal.hotels.forEach((hotel) => {
    const city = getCity(hotel);
    const checkIn = getCheckIn(hotel);
    const checkOut = getCheckOut(hotel);
    const key = getSlotKey(city, checkIn, checkOut);
    const current = slotHotelsMap.get(key) || [];
    current.push(hotel);
    slotHotelsMap.set(key, current);
  });

  const flightModels = proposal.flights.map((flight, index) => ({
    flight,
    index,
    beforeData: getBeforeFlightData(flight),
    afterData: getAfterFlightData(flight),
    legs: getFlightLegs(flight),
  }));
  const transferModels = proposal.transfers.map((transfer, index) =>
    getTransferData(transfer, index),
  );

  const chainNodes = slots
    .map((slot) => ({
      slot,
      hotels: slotHotelsMap.get(slot.key) || [],
      transfers: transferModels.filter(
        (transferModel) =>
          transferModel.day >= toDayKey(slot.checkIn) &&
          transferModel.day <= toDayKey(slot.checkOut) &&
          isSameCity(transferModel.city, slot.city),
      ),
    }))
    .filter((node) => node.hotels.length > 0);

  if (!chainNodes.length) {
    return [];
  }

  const isWholeTripFlightFit = (
    flightModel: (typeof flightModels)[number],
    chainNodes: Array<{ slot: TripSlot; hotels: HotProposal[] }>,
  ) => {
    if (flightModel.legs.length <= 1 || !chainNodes.length) {
      return true;
    }

    const firstSlot = chainNodes[0].slot;
    const lastSlot = chainNodes[chainNodes.length - 1].slot;
    const requiredLegMatchers = [
      (leg: (typeof flightModel.legs)[number]) => leg.arrivalDay === toDayKey(firstSlot.checkIn),
      (leg: (typeof flightModel.legs)[number]) => leg.departureDay === toDayKey(lastSlot.checkOut),
    ];
    const optionalMiddleLegMatchers = chainNodes.slice(0, -1).map((node, index) => {
      const nextNode = chainNodes[index + 1];
      return (leg: (typeof flightModel.legs)[number]) =>
        leg.departureDay === toDayKey(nextNode.slot.checkIn);
    });
    const allowedLegMatchers = [...requiredLegMatchers, ...optionalMiddleLegMatchers];

    const hasRequiredLegs = requiredLegMatchers.every((matcher) =>
      flightModel.legs.some((leg: any) => matcher(leg)),
    );
    if (!hasRequiredLegs) {
      return false;
    }

    return flightModel.legs.every((leg: any) => allowedLegMatchers.some((matcher) => matcher(leg)));
  };

  const firstSlot = chainNodes[0].slot;
  const lastSlot = chainNodes[chainNodes.length - 1].slot;
  const chainFlightModels = flightModels.filter((flightModel) =>
    isWholeTripFlightFit(flightModel, chainNodes),
  );
  const suitableRoundTrips = chainFlightModels
    .filter(
      (flightModel) =>
        flightModel.beforeData.isRoundTrip &&
        flightModel.beforeData.day === toDayKey(firstSlot.checkIn) &&
        flightModel.afterData.day === toDayKey(lastSlot.checkOut),
    )
    .map((flightModel) => ({
      flight: flightModel.flight,
      index: flightModel.index,
      beforeData: flightModel.beforeData,
      afterData: flightModel.afterData,
    }));
  const suitableRoundTripKeys = new Set(
    suitableRoundTrips.map((flightModel) => getFlightKey(flightModel.flight, flightModel.index)),
  );
  const beforeCandidates = chainFlightModels
    .filter(
      (flightModel) =>
        (!flightModel.beforeData.isRoundTrip ||
          suitableRoundTripKeys.has(getFlightKey(flightModel.flight, flightModel.index))) &&
        flightModel.beforeData.day === toDayKey(firstSlot.checkIn),
    )
    .map((flightModel) => ({
      flight: flightModel.flight,
      index: flightModel.index,
      route: flightModel.beforeData.route,
      isRoundTrip: flightModel.beforeData.isRoundTrip,
    }));
  const afterCandidates = chainFlightModels
    .filter(
      (flightModel) =>
        (!flightModel.beforeData.isRoundTrip ||
          suitableRoundTripKeys.has(getFlightKey(flightModel.flight, flightModel.index))) &&
        flightModel.afterData.day === toDayKey(lastSlot.checkOut),
    )
    .map((flightModel) => ({
      flight: flightModel.flight,
      index: flightModel.index,
      route: flightModel.afterData.route,
      isRoundTrip: flightModel.beforeData.isRoundTrip,
    }));

  const middleFlightsByTransition = chainNodes.reduce(
    (acc, node, index) => {
      const nextNode = chainNodes[index + 1];
      if (!nextNode) {
        return acc;
      }
      if (toDayKey(node.slot.checkOut) !== toDayKey(nextNode.slot.checkIn)) {
        return acc;
      }
      const transitionKey = `${node.slot.key}|${nextNode.slot.key}`;
      const transitionDay = toDayKey(nextNode.slot.checkIn);
      const candidates = chainFlightModels.flatMap((flightModel): TripBuilderFlightCandidate[] => {
        if (
          flightModel.beforeData.isRoundTrip &&
          !suitableRoundTripKeys.has(getFlightKey(flightModel.flight, flightModel.index))
        ) {
          return [];
        }
        const matchingLeg = flightModel.legs.find((leg: any) => leg.departureDay === transitionDay);
        if (!matchingLeg) {
          return [];
        }
        return [
          {
            flight: flightModel.flight,
            index: flightModel.index,
            route: matchingLeg.route,
            airlineCode: matchingLeg.airlineCode,
            isRoundTrip: flightModel.beforeData.isRoundTrip,
          },
        ];
      });
      acc[transitionKey] = candidates;
      return acc;
    },
    {} as Record<string, TripBuilderFlightCandidate[]>,
  );

  return [
    {
      rootSlotKey: firstSlot.key,
      chainNodes,
      suitableRoundTrips,
      beforeCandidates,
      afterCandidates,
      middleFlightsByTransition,
    },
  ];
};

export const filterDisplayedProposal = (
  proposal: Proposal,
  slotContexts: TripSlotContext[],
): Proposal => {
  const displayedFlights = new Set<FlProposal>();
  const displayedHotels = new Set<HotProposal>();
  const displayedTransfers = new Set<TransferProposal>();

  slotContexts.forEach((context) => {
    context.beforeCandidates.forEach((candidate) => displayedFlights.add(candidate.flight));
    context.afterCandidates.forEach((candidate) => displayedFlights.add(candidate.flight));
    Object.values(context.middleFlightsByTransition).forEach((candidates) =>
      candidates.forEach((candidate) => displayedFlights.add(candidate.flight)),
    );
    context.chainNodes.forEach((node) => {
      node.hotels.forEach((hotel) => displayedHotels.add(hotel));
      node.transfers.forEach((transferModel) => displayedTransfers.add(transferModel.transfer));
    });
  });

  return {
    ...proposal,
    flights: proposal.flights.filter((flight) => displayedFlights.has(flight)),
    hotels: proposal.hotels.filter((hotel) => displayedHotels.has(hotel)),
    dynamic: [],
    ilPackages: [],
    transfers: proposal.transfers.filter((transfer) => displayedTransfers.has(transfer)),
  };
};

export const buildPTypeBySearchToken = (
  slotContexts: TripSlotContext[],
  rows: Record<string, TripSlotRequirement>,
): Record<string, NonNullable<ProductProposal['pType']>> => {
  const map: Record<string, NonNullable<ProductProposal['pType']>> = {};

  const assign = (searchToken: string, rowKey: string, fallback: TripSlotRequirement) => {
    const requirement = rows[rowKey] ?? fallback;
    map[searchToken] = tripRequirementToPType(requirement);
  };

  slotContexts.forEach(
    ({ rootSlotKey, chainNodes, beforeCandidates, afterCandidates, middleFlightsByTransition }) => {
      if (beforeCandidates.length) {
        const rowKey = tripRowKeys.before(rootSlotKey);
        beforeCandidates.forEach((candidate) =>
          assign(candidate.flight.searchToken, rowKey, defaultTripSlotRequirement),
        );
      }
      chainNodes.forEach((node) => {
        if (!node.hotels.length) {
          return;
        }
        const rowKey = tripRowKeys.hotel(node.slot.key);
        node.hotels.forEach((hotel) =>
          assign(hotel.searchToken, rowKey, defaultTripSlotRequirement),
        );
        if (node.transfers.length) {
          const transferRowKey = tripRowKeys.transfer(node.slot.key);
          node.transfers.forEach((transferModel) =>
            assign(transferModel.transfer.searchToken, transferRowKey, defaultTripSlotRequirement),
          );
        }
      });
      Object.entries(middleFlightsByTransition).forEach(([transitionKey, candidates]) => {
        if (!candidates.length) {
          return;
        }
        const rowKey = tripRowKeys.middle(transitionKey);
        candidates.forEach((candidate) => assign(candidate.flight.searchToken, rowKey, 'optional'));
      });
      if (afterCandidates.length) {
        const rowKey = tripRowKeys.after(rootSlotKey);
        afterCandidates.forEach((candidate) =>
          assign(candidate.flight.searchToken, rowKey, defaultTripSlotRequirement),
        );
      }
    },
  );

  return map;
};

export const buildHotelSlotKeyBySearchToken = (
  slotContexts: TripSlotContext[],
): Record<string, string> => {
  const map: Record<string, string> = {};
  slotContexts.forEach(({ chainNodes }) => {
    chainNodes.forEach((node) => {
      node.hotels.forEach((hotel) => {
        map[hotel.searchToken] = node.slot.key;
      });
    });
  });
  return map;
};

export const buildTripBuilderAdditionalInfoParams = (
  values: TripBuilderAdditionalInfoValues,
  options?: { existingPasswordData?: string },
): Pick<CreateProposalParams, 'paxes' | 'internalData' | 'expireDate'> => {
  const params: Pick<CreateProposalParams, 'paxes' | 'internalData' | 'expireDate'> = {
    paxes: [
      {
        firstName: values.firstName,
        lastName: ' ',
        eMailAddress: values.email,
      },
    ],
    expireDate: toTripBuilderExpireDateTime(values.expireDate),
  };

  const internalData = buildTripBuilderInternalData(values, options?.existingPasswordData);
  if (internalData) {
    params.internalData = internalData;
  }

  return params;
};

export const buildTripBuilderAdditionalInfoUpdateParams = (
  values: TripBuilderAdditionalInfoValues,
  proposal?: {
    paxes?: Array<Record<string, unknown>>;
    listOfPaxes?: Array<Record<string, unknown>> | null;
    externalParam_2?: string;
    expireDate?: string;
  },
): Pick<CreateProposalParams, 'paxes'> &
  Partial<Pick<CreateProposalParams, 'internalData' | 'expireDate'>> => {
  const saved = mapProposalToTripBuilderAdditionalInfo(proposal);
  const params: Pick<CreateProposalParams, 'paxes'> &
    Partial<Pick<CreateProposalParams, 'internalData' | 'expireDate'>> = {
    paxes: [
      {
        firstName: values.firstName,
        lastName: ' ',
        eMailAddress: values.email,
      },
    ],
  };

  const expireDateChanged =
    toTripBuilderExpireDateTime(values.expireDate) !==
    toTripBuilderExpireDateTime(saved.expireDate);

  if (expireDateChanged) {
    params.expireDate = toTripBuilderExpireDateTime(values.expireDate);
  }

  if (values.password.trim()) {
    const internalData = buildTripBuilderInternalData(values, proposal?.externalParam_2);
    if (internalData) {
      params.internalData = internalData;
    }
  }

  return params;
};

export type TripBuilderAdditionalInfoValues = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  expireDate: string;
};

const parseTripBuilderInternalData = (externalParam_2?: string): Record<string, unknown> => {
  if (!externalParam_2) {
    return {};
  }

  try {
    const parsed = JSON.parse(externalParam_2);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const getDefaultTripBuilderExpireDate = (): string => {
  const maxDate = addDays(7);
  maxDate.setHours(0, 0, 0, 0);
  return dateFormat(maxDate, 'yyyy-MM-dd', 'en');
};

export const getTripBuilderExpireDateBounds = () => {
  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);
  const maxDate = addDays(7);
  maxDate.setHours(0, 0, 0, 0);
  return {
    minDate,
    maxDate,
  };
};

export const parseTripBuilderExpireDate = (value?: string): Date | undefined => {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const normalizeTripBuilderExpireDateForPicker = (value?: string): string => {
  const date = parseTripBuilderExpireDate(value);
  if (!date) {
    return getDefaultTripBuilderExpireDate();
  }

  return dateFormat(date, 'yyyy-MM-dd', 'en');
};

export const toTripBuilderExpireDateTime = (value?: string): string => {
  const date = parseTripBuilderExpireDate(value);
  if (!date) {
    return '';
  }

  return formatISO(date).replace(/([+-]\d{2}:\d{2})$/, 'Z');
};

const buildTripBuilderInternalData = (
  values: TripBuilderAdditionalInfoValues,
  existingExternalParam_2?: string,
): string | undefined => {
  const internalData: Record<string, unknown> = {
    ...parseTripBuilderInternalData(existingExternalParam_2),
  };
  delete internalData.expireDate;

  if (values.password.trim()) {
    internalData.password = md5(values.password);
  }

  if (!Object.keys(internalData).length) {
    return undefined;
  }

  return JSON.stringify(internalData);
};

const readPaxString = (pax: Record<string, unknown> | undefined, ...keys: string[]) => {
  if (!pax) {
    return '';
  }

  for (const key of keys) {
    const value = pax[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
};

export const getTripBuilderPasswordStorage = (proposal?: { externalParam_2?: string }) =>
  proposal?.externalParam_2;

export const hasTripBuilderSavedPassword = (proposal?: { externalParam_2?: string }) => {
  const storage = getTripBuilderPasswordStorage(proposal);
  if (!storage) {
    return false;
  }

  try {
    const parsed = JSON.parse(storage) as { password?: string };
    return !!parsed?.password;
  } catch {
    return false;
  }
};

export const getTripBuilderPasswordHash = (proposal?: { externalParam_2?: string }) => {
  const storage = getTripBuilderPasswordStorage(proposal);
  if (!storage) {
    return '';
  }

  try {
    const parsed = JSON.parse(storage) as { password?: string };
    return parsed?.password || '';
  } catch {
    return '';
  }
};

export const verifyTripBuilderPassword = (
  proposal: { externalParam_2?: string },
  password: string,
) => {
  const hash = getTripBuilderPasswordHash(proposal);
  return !!hash && md5(password) === hash;
};

export const getTripBuilderUnlockStorageKey = (proposalId: string) => `tb-unlock-${proposalId}`;

export const isTripBuilderUnlocked = (proposalId: string) =>
  sessionStorage.getItem(getTripBuilderUnlockStorageKey(proposalId)) === '1';

export const setTripBuilderUnlocked = (proposalId: string) =>
  sessionStorage.setItem(getTripBuilderUnlockStorageKey(proposalId), '1');

export const mapProposalToTripBuilderAdditionalInfo = (proposal?: {
  listOfPaxes?: Array<Record<string, unknown>> | null;
  externalParam_2?: string;
  expireDate?: string;
}): TripBuilderAdditionalInfoValues => {
  const paxList = proposal?.listOfPaxes;
  const pax = Array.isArray(paxList)
    ? (paxList[0] as Record<string, unknown> | undefined)
    : undefined;
  const internalData = parseTripBuilderInternalData(proposal?.externalParam_2);
  const expireDateSource =
    proposal?.expireDate ||
    (typeof internalData.expireDate === 'string' ? internalData.expireDate : undefined);

  return {
    firstName: readPaxString(pax, 'firstName', 'FirstName'),
    lastName: ' ',
    email: readPaxString(pax, 'eMailAddress', 'email', 'EMailAddress', 'Email'),
    password: '',
    expireDate: normalizeTripBuilderExpireDateForPicker(expireDateSource),
  };
};

export const getTripBuilderClientName = (proposal?: {
  listOfPaxes?: Array<Record<string, unknown>> | null;
}) => {
  const { firstName, lastName } = mapProposalToTripBuilderAdditionalInfo(proposal);
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return name || '–';
};

const collectTripBuilderServiceDates = (values: (string | undefined)[]): Date | undefined => {
  const timestamps: number[] = [];

  values.forEach((value) => {
    if (!value) {
      return;
    }

    const time = new Date(value).getTime();
    if (!Number.isNaN(time)) {
      timestamps.push(time);
    }
  });

  if (!timestamps.length) {
    return undefined;
  }

  return new Date(Math.min(...timestamps));
};

export const formatTripBuilderServiceDate = (date?: Date) =>
  date ? dateFormat(date, 'dd-MMM-yyyy', 'en').toUpperCase() : '';

export type TripBuilderExpireDateHeaderParts = {
  daysLabel: string;
  datePart: string;
  isExpired: boolean;
};

export const getTripBuilderExpireDateHeaderParts = (
  value?: string,
): TripBuilderExpireDateHeaderParts | null => {
  const date = parseTripBuilderExpireDate(value);
  if (!date || date.getFullYear() < 1900) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expireDay = new Date(date);
  expireDay.setHours(0, 0, 0, 0);

  const diffDays = Math.round((expireDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  const daysLabel = `${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`;

  const datePart = dateFormat(date, 'dd-MMM-yyyy', 'en').toUpperCase();

  return {
    daysLabel,
    datePart,
    isExpired: diffDays < 0,
  };
};

export const getTripBuilderFirstServiceDateFromDraft = (
  proposal: Pick<Proposal, 'hotels' | 'flights' | 'transfers'>,
): Date | undefined => {
  const dates: (string | undefined)[] = [];

  proposal.hotels?.forEach((hotel) => dates.push(getCheckIn(hotel)));
  proposal.flights?.forEach((flight) => {
    const bounds = getBounds(flight.item);
    dates.push(bounds[0]?.departureDateTime);
  });
  proposal.transfers?.forEach((transfer) => {
    const forward = transfer.forwardItem?.transfer;
    dates.push(forward?.pickUpDate || forward?.pickUpDetails?.pickUpDateTime);
    const returnTransfer = transfer.returnItem?.transfer;
    dates.push(returnTransfer?.pickUpDate || returnTransfer?.pickUpDetails?.pickUpDateTime);
  });

  return collectTripBuilderServiceDates(dates);
};

export const getTripBuilderFirstServiceDateFromProposalData = (
  proposal: ProposalData,
): Date | undefined => {
  const dates: (string | undefined)[] = [];

  proposal.listOfProducts?.forEach((product) => {
    if (product.removed) {
      return;
    }

    product.listOfItems?.forEach((item) => {
      item.hotels?.forEach((hotel) => {
        hotel.rooms?.forEach((room) => dates.push(room.startDate));
      });
      item.flights?.forEach((flight) => {
        dates.push(flight.departureDateTime);
        flight.gdsFlyAirs?.forEach((segment) => dates.push(segment.departureDateTime));
      });
      item.transfers?.forEach((transfer) => dates.push(transfer.pickUpDate));
    });
  });

  return collectTripBuilderServiceDates(dates);
};

export const buildTripBuilderCreateParams = (
  proposal: Proposal,
  rows: Record<string, TripSlotRequirement>,
  displayTotalDelta: boolean,
  base: Pick<CreateProposalParams, 'comments' | 'recaptchaToken' | 'inputData' | 'description'>,
  hotelImageBySlot?: Record<string, string>,
  cancellationSettings?: {
    showCancellationDate: boolean;
    cancellationDateDelta: number;
    language?: string;
  },
): CreateProposalParams => {
  const slotContexts = buildTripSlotContexts(proposal);
  const filteredProposal = filterDisplayedProposal(proposal, slotContexts);
  const pTypeBySearchToken = buildPTypeBySearchToken(slotContexts, rows);

  return {
    ...base,
    reportsList: getReportList(filteredProposal, {
      pTypeBySearchToken,
      hotelImageBySlot,
      hotelSlotKeyBySearchToken: buildHotelSlotKeyBySearchToken(slotContexts),
      showCancellationDate: cancellationSettings?.showCancellationDate,
      cancellationDateDelta: cancellationSettings?.cancellationDateDelta,
      language: cancellationSettings?.language,
    }),
    displayTotalDelta,
    activeLink: true,
  };
};

export type IdbDraftProducts = {
  hotels: HotProposal[];
  flights: FlProposal[];
  transfers: TransferProposal[];
};

export const mergeIdbDraftsIntoProposal = (
  proposal: Proposal,
  idbDrafts?: Partial<IdbDraftProducts>,
): Proposal => {
  if (!idbDrafts) {
    return proposal;
  }

  const existingHotelCodes = new Set(
    proposal.hotels.map((hotel) => hotel.item?.itemCode).filter(Boolean) as string[],
  );
  const existingFlightKeys = new Set(
    proposal.flights
      .map((flight) => flight.item?.id || flight.searchToken)
      .filter(Boolean) as string[],
  );
  const existingTransferKeys = new Set(
    proposal.transfers
      .map((transfer) => transfer.forwardItem?.priceKey || transfer.searchToken)
      .filter(Boolean) as string[],
  );

  const hotels = [...proposal.hotels];
  (idbDrafts.hotels || []).forEach((hotel) => {
    const itemCode = hotel.item?.itemCode;
    if (itemCode && existingHotelCodes.has(itemCode)) {
      return;
    }
    if (itemCode) {
      existingHotelCodes.add(itemCode);
    }
    hotels.push(hotel);
  });

  const flights = [...proposal.flights];
  (idbDrafts.flights || []).forEach((flight) => {
    const key = flight.item?.id || flight.searchToken;
    if (!key || existingFlightKeys.has(key)) {
      return;
    }
    existingFlightKeys.add(key);
    flights.push(flight);
  });

  const transfers = [...proposal.transfers];
  (idbDrafts.transfers || []).forEach((transfer) => {
    const key = transfer.forwardItem?.priceKey || transfer.searchToken;
    if (!key || existingTransferKeys.has(key)) {
      return;
    }
    existingTransferKeys.add(key);
    transfers.push(transfer);
  });

  return {
    ...proposal,
    hotels,
    flights,
    transfers,
  };
};

export const isTripBuilderProposal = (
  proposal?: ProposalData,
  plainPreview?: boolean,
  asTripBuilder?: boolean,
) => {
  if (plainPreview) {
    return false;
  }
  if (asTripBuilder) {
    return true;
  }
  if (!proposal?.externalParam_1) {
    return false;
  }
  try {
    return JSON.parse(proposal.externalParam_1).isTripBuilder === true;
  } catch {
    return false;
  }
};

export const buildProposalUpdateInputData = (
  existingProposal: ProposalData,
  options: { template?: string; sort?: string; language: string },
): string | undefined => {
  if (!existingProposal.externalParam_1) {
    return undefined;
  }

  let existing: Record<string, unknown>;
  try {
    existing = JSON.parse(existingProposal.externalParam_1);
  } catch {
    return undefined;
  }

  const overrides: Record<string, unknown> = {};
  if (options.template && existing.template !== options.template) {
    overrides.template = options.template;
  }
  if (options.sort && existing.sort !== options.sort) {
    overrides.sort = options.sort;
  }
  if (existing.language !== options.language) {
    overrides.language = options.language;
  }

  const isTripBuilder = existing.isTripBuilder === true;
  const externalParamEdited = !!(existingProposal as { externalParamEdited?: boolean })
    .externalParamEdited;

  if (isTripBuilder) {
    if (!externalParamEdited && Object.keys(overrides).length === 0) {
      return undefined;
    }

    return JSON.stringify({ ...existing, ...overrides });
  }

  return JSON.stringify({
    ...existing,
    ...(options.template ? { template: options.template } : {}),
    language: options.language,
    ...(options.sort ? { sort: options.sort } : {}),
  });
};
